import type {
  GameContext,
  GameInstance,
  GameModule,
  PreloadContext,
} from '@arcraz/arcadia-types';
import { CargoClimbInstance } from './instance.js';
import { PALETTE_HINT } from './palette.js';

/**
 * CARGO CLIMB — fourth cartridge.
 *
 * Single-screen climbing platformer. Donkey Kong + Burger Time heritage.
 * The original mechanic is **mid-air crate catch** — jump UP into a
 * falling crate and ride it down to its landing platform. Risky
 * shortcut: you can't bail mid-ride, the crate's bounce arc is what
 * kills you.
 *
 * Resolution: 480x270 landscape per the cabinet's landscape-only rule.
 * The playfield is a centered 270-wide portrait column; the side
 * gutters are cartridge-owned HUD + industrial chrome (left: SCORE /
 * LEVEL / LIVES / NEXT-CRATE preview / crane alert; right: scaffolding
 * extension + crane silhouette + hazard stripe).
 *
 * MVP scope (sc-3126):
 *   - 6 platforms × 5 ladders (alternating sides for climb rhythm)
 *   - 3 crate tiers (square / long / L-shape) with tier-specific
 *     bounce + horizontal speed; L-shape can re-roll direction at
 *     platform bounces
 *   - mid-air crate catch — latch when vy <= -0.5 and player top
 *     crosses crate bottom
 *   - 3 lives, 60-tick respawn invuln
 *   - level progression: reach top platform → bonus, restart at bottom
 *     with faster crane spawn cadence + more L-shape rolls
 */
const module_: GameModule = {
  id: 'cargo-climb',
  title: 'CARGO CLIMB',
  manufacturer: 'ARCADIA AMUSEMENTS',
  year: 1984,
  genre: 'platform',
  resolution: { w: 480, h: 270 },
  pixelated: true,
  paletteHint: PALETTE_HINT,
  validatorVersion: 1,
  controls: [
    { keys: 'LEFT / RIGHT', action: 'WALK' },
    { keys: 'UP / DOWN', action: 'CLIMB LADDER' },
    { keys: 'Z / SPACE', action: 'JUMP' },
    { keys: 'Z', action: 'RESTART (AT GAME OVER)' },
  ],

  async preload(ctx: PreloadContext): Promise<void> {
    // Jump — short rising blip. Frequent input so kept quiet and short.
    ctx.registerSfx('jump', {
      wave: 'square',
      pitch: [{ t: 0, f: 500 }, { t: 22, f: 780 }],
      gain: [{ t: 0, g: 0 }, { t: 3, g: 0.1 }, { t: 22, g: 0.0 }],
    });
    // Land — soft thunk. Avoids fatiguing the ear during the climb-
    // jump-climb rhythm.
    ctx.registerSfx('land', {
      wave: 'sine',
      pitch: [{ t: 0, f: 200 }, { t: 40, f: 110 }],
      gain: [{ t: 0, g: 0 }, { t: 4, g: 0.14 }, { t: 40, g: 0.0 }],
    });
    // Crate bounce — low knock at the moment a crate hits a platform.
    ctx.registerSfx('crate-bounce', {
      wave: 'triangle',
      pitch: [{ t: 0, f: 160 }, { t: 50, f: 90 }],
      gain: [{ t: 0, g: 0 }, { t: 4, g: 0.18 }, { t: 50, g: 0.0 }],
    });
    // Crate hit — full crash on player death.
    ctx.registerSfx('crate-hit', {
      wave: 'noise',
      pitch: [{ t: 0, f: 100 }, { t: 200, f: 40 }],
      gain: [{ t: 0, g: 0 }, { t: 6, g: 0.55 }, { t: 200, g: 0.0 }],
    });
    // Level up — ascending fanfare matching the cabinet's musical
    // language across cartridges (re-uses Stardust's wave-up shape).
    ctx.registerSfx('level-up', {
      wave: 'square',
      pitch: [
        { t: 0, f: 440 },
        { t: 100, f: 660 },
        { t: 200, f: 880 },
      ],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.3 }, { t: 200, g: 0.0 }],
    });
    // Crane warning — short warble before each crate spawn. Plays once
    // per warn window so the player can preempt the next dodge.
    ctx.registerSfx('crane-spawn', {
      wave: 'sawtooth',
      pitch: [{ t: 0, f: 280 }, { t: 80, f: 220 }, { t: 160, f: 280 }],
      gain: [{ t: 0, g: 0 }, { t: 6, g: 0.18 }, { t: 160, g: 0.0 }],
      fm: { ratio: 0.5, index: 25 },
    });
  },

  init(ctx: GameContext): GameInstance {
    return new CargoClimbInstance(ctx);
  },
};

export default module_;
